import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { ApiError } from "../../lib/api";
import { crmApi } from "../../lib/crm-api";
import { queryKeys } from "../../lib/queryKeys";
import { relativeTime, shortDateTime } from "../../lib/time";
import type {
  WebhookDelivery,
  WebhookEndpoint,
  WebhookEventType,
  WebhookMode,
} from "../../lib/crm-types";
import { Button } from "../../components/ui/Button";
import { CopyButton } from "../../components/CopyButton";
import { cn } from "../../components/ui/cn";

/**
 * Webhook endpoints: telling another system what changed in the CRM.
 *
 * The page is built around the three things an admin actually does here, in the
 * order they do them: add an endpoint and pick its events, check it works, and
 * later find out why the receiver says it never got something.
 *
 * Two shapes in the data are worth reading before changing this. An endpoint
 * subscribes to a *set* of events, so the same change can go to several
 * receivers and one receiver can take several kinds of change -- that is why the
 * event picker is checkboxes on each endpoint rather than a single dropdown, and
 * why there is no notion of a "primary" endpoint. And deliveries are batched: a
 * row in the history is one POST carrying several events, not one event, which
 * is why the history expands rather than listing flat.
 *
 * A subscription is either pushed to or pulled from, fixed when it is added.
 * A pull subscription has no URL: its reader fetches `/v1/crm/feeds/{id}/events`
 * with the secret shown here and acknowledges what it handled, so its row shows
 * when it was last read and how much is waiting rather than when it was sent to.
 * Its history rows are its acknowledgements.
 */

/** A reader that has not asked for this long is worth a second look. */
const STALE_READER_MS = 15 * 60 * 1000;

/** How each subscribable type is described to somebody choosing it. */
const EVENT_LABELS: Record<WebhookEventType, string> = {
  "company.created": "Company created",
  "company.updated": "Company updated",
  "company.deleted": "Company deleted",
  "company.merged": "Company merged",
  "contact.created": "Contact created",
  "contact.updated": "Contact updated",
  "contact.deleted": "Contact removed",
};

/**
 * The two that need a caveat, because the CRM does not literally delete either.
 * Said here rather than in a doc nobody opens: this is where the box is ticked.
 */
const EVENT_HINTS: Partial<Record<WebhookEventType, string>> = {
  "company.deleted":
    "Fires when a company is merged away. Carries mergedIntoCompanyId so you can repoint rather than drop.",
  "company.merged": "Fires on the surviving company, naming what was merged in.",
  "contact.deleted":
    "Fires when somebody is removed from a company. The person may still exist unfiled.",
  "contact.updated":
    "Includes moves between companies, with fromCompanyId in the payload.",
};

export function WebhookSettings() {
  const queryClient = useQueryClient();
  const [adding, setAdding] = useState(false);

  const settings = useQuery({
    queryKey: queryKeys.crmWebhooks,
    queryFn: crmApi.webhooks,
    staleTime: 30_000,
  });

  const refresh = (): void => {
    void queryClient.invalidateQueries({ queryKey: queryKeys.crmWebhooks });
  };

  const flush = useMutation({
    mutationFn: crmApi.flushWebhooks,
    onSuccess: refresh,
  });

  const endpoints = settings.data?.endpoints ?? [];
  const eventTypes = settings.data?.eventTypes ?? [];
  /* Push only: "Send now" cannot move a feed's backlog -- its reader does. */
  const waiting = endpoints
    .filter((row) => row.mode === "push")
    .reduce((total, row) => total + row.pendingEvents, 0);

  return (
    <section>
      {/* No heading of its own: this is a collapsible panel now, and the panel
          already carries the word "Webhooks" as its title. */}
      <div className="mb-2 flex flex-wrap items-center gap-2">
        <button
          type="button"
          onClick={() => setAdding((value) => !value)}
          className="text-xs text-brand-600 hover:text-brand-700"
        >
          {adding ? "Cancel" : "Add endpoint"}
        </button>

        {waiting > 0 ? (
          <>
            <span className="text-[11px] text-faint">
              {waiting} {waiting === 1 ? "change" : "changes"} waiting for the
              next batch
            </span>
            <button
              type="button"
              onClick={() => flush.mutate()}
              disabled={flush.isPending}
              className="text-xs text-brand-600 hover:text-brand-700"
            >
              {flush.isPending ? "Sending…" : "Send now"}
            </button>
          </>
        ) : null}
      </div>

      <p className="mb-2 max-w-3xl text-[11px] text-faint">
        Changes are gathered for a minute and sent as one POST carrying a list of
        events, so a busy afternoon is a handful of requests rather than
        hundreds. Every event has an <code>id</code> that stays the same across
        retries — deduplicate on it, because a delivery we never saw succeed is
        sent again. Each request is signed with the endpoint&rsquo;s secret in{" "}
        <code>X-Helpdesk-Signature</code>.
      </p>

      {adding ? (
        <NewEndpointForm
          eventTypes={eventTypes}
          onDone={() => {
            setAdding(false);
            refresh();
          }}
        />
      ) : null}

      <ul className="flex flex-col gap-2">
        {endpoints.length === 0 ? (
          <li className="rounded-lg border border-line px-3 py-4 text-xs text-faint">
            No endpoints. Nothing is recorded until one exists, so adding the
            first one starts from now rather than replaying history.
          </li>
        ) : (
          endpoints.map((endpoint) => (
            <EndpointRow
              key={endpoint.id}
              endpoint={endpoint}
              eventTypes={eventTypes}
              onChange={refresh}
            />
          ))
        )}
      </ul>
    </section>
  );
}

// ── One endpoint ─────────────────────────────────────────────────────────────

function EndpointRow({
  endpoint,
  eventTypes,
  onChange,
}: {
  endpoint: WebhookEndpoint;
  eventTypes: WebhookEventType[];
  onChange: () => void;
}) {
  const [open, setOpen] = useState(false);
  const [showSecret, setShowSecret] = useState(false);
  const [name, setName] = useState(endpoint.name);
  const [url, setUrl] = useState(endpoint.url ?? "");
  const pull = endpoint.mode === "pull";

  const update = useMutation({
    mutationFn: (patch: Parameters<typeof crmApi.updateWebhook>[1]) =>
      crmApi.updateWebhook(endpoint.id, patch),
    onSuccess: onChange,
  });

  const rotate = useMutation({
    mutationFn: () => crmApi.rotateWebhookSecret(endpoint.id),
    onSuccess: () => {
      setShowSecret(true);
      onChange();
    },
  });

  const remove = useMutation({
    mutationFn: () => crmApi.deleteWebhook(endpoint.id),
    onSuccess: onChange,
  });

  const test = useMutation({ mutationFn: () => crmApi.testWebhook(endpoint.id) });

  const toggleEvent = (type: WebhookEventType): void => {
    const next = endpoint.events.includes(type)
      ? endpoint.events.filter((event) => event !== type)
      : [...endpoint.events, type];

    // The API refuses an empty subscription; say so here rather than round-trip
    // to a 400 that the checkbox cannot explain.
    if (next.length === 0) return;
    update.mutate({ events: next });
  };

  return (
    <li
      className={cn(
        "rounded-lg border border-line",
        !endpoint.isActive && "opacity-60",
      )}
    >
      <div className="flex flex-wrap items-center gap-2 px-3 py-2">
        <input
          value={name}
          onChange={(event) => setName(event.target.value)}
          onBlur={() => name !== endpoint.name && update.mutate({ name })}
          aria-label={`Name for endpoint ${endpoint.id}`}
          className="h-7 w-40 rounded border border-transparent bg-transparent px-1 text-sm font-medium text-content hover:border-line focus:border-brand-400 focus:outline-none"
        />

        {pull ? (
          <span
            className="min-w-0 flex-1 truncate px-1 font-mono text-xs text-muted"
            title="Its reader fetches this with the feed secret, and acknowledges what it handled."
          >
            GET /api/v1/crm/feeds/{endpoint.id}/events
          </span>
        ) : (
          <input
            value={url}
            onChange={(event) => setUrl(event.target.value)}
            onBlur={() => url !== endpoint.url && update.mutate({ url })}
            aria-label={`URL for ${endpoint.name}`}
            className="h-7 min-w-0 flex-1 rounded border border-transparent bg-transparent px-1 font-mono text-xs text-muted hover:border-line focus:border-brand-400 focus:outline-none"
          />
        )}

        <Health endpoint={endpoint} />

        <button
          type="button"
          onClick={() => setOpen((value) => !value)}
          className="text-xs text-brand-600 hover:text-brand-700"
        >
          {open ? "Hide" : "Details"}
        </button>
      </div>

      <div className="flex flex-wrap items-center gap-1 border-t border-line px-3 py-2">
        {eventTypes.map((type) => {
          const on = endpoint.events.includes(type);
          return (
            <button
              key={type}
              type="button"
              onClick={() => toggleEvent(type)}
              disabled={update.isPending}
              title={EVENT_HINTS[type] ?? `Send ${type} to this endpoint`}
              className={cn(
                "rounded-full border px-2 py-0.5 text-[11px]",
                on
                  ? "border-brand-400 bg-brand-50 text-brand-700 dark:border-brand-700 dark:bg-brand-950 dark:text-brand-300"
                  : "border-line text-faint hover:border-brand-300 hover:text-muted",
              )}
            >
              {EVENT_LABELS[type] ?? type}
            </button>
          );
        })}
      </div>

      {update.isError ? (
        <p className="px-3 pb-2 text-xs text-sla-breached">
          {update.error instanceof ApiError
            ? update.error.message
            : "Could not save that change."}
        </p>
      ) : null}

      {open ? (
        <div className="border-t border-line px-3 py-2">
          <div className="mb-3 flex flex-wrap items-center gap-2">
            <span className="text-[11px] uppercase tracking-wide text-faint">
              {pull ? "Feed secret" : "Signing secret"}
            </span>

            {showSecret ? (
              <CopyButton
                value={endpoint.secret}
                what={pull ? "feed secret" : "signing secret"}
                className="font-mono text-[11px]"
              />
            ) : (
              <button
                type="button"
                onClick={() => setShowSecret(true)}
                className="text-xs text-brand-600 hover:text-brand-700"
              >
                Reveal
              </button>
            )}

            <button
              type="button"
              onClick={() => rotate.mutate()}
              disabled={rotate.isPending}
              title={
                pull
                  ? "Issues a new secret. The reader is refused until it has it."
                  : "Issues a new secret. Deliveries stop verifying until the receiver has it."
              }
              className="text-xs text-muted hover:text-brand-600 dark:hover:text-brand-400"
            >
              {rotate.isPending ? "Rotating…" : "Rotate"}
            </button>

            {pull ? null : (
              <Button
                size="sm"
                variant="secondary"
                disabled={test.isPending}
                onClick={() => test.mutate()}
              >
                {test.isPending ? "Sending…" : "Send test"}
              </Button>
            )}

            {test.data ? (
              <span
                className={cn(
                  "text-xs",
                  test.data.ok ? "text-sla-ok" : "text-sla-breached",
                )}
              >
                {test.data.ok
                  ? `Accepted (HTTP ${test.data.status})`
                  : (test.data.error ?? "Rejected")}
              </span>
            ) : null}

            <button
              type="button"
              onClick={() => update.mutate({ isActive: !endpoint.isActive })}
              title={
                pull
                  ? endpoint.isActive
                    ? "Stops queueing changes for this feed. What is already waiting can still be read."
                    : "Starts queueing changes again. Anything that happened while paused is not in the feed."
                  : endpoint.isActive
                    ? "Holds delivery. Changes keep queueing and go out when you resume."
                    : "Resumes delivery, starting with whatever queued while paused."
              }
              className="ml-auto text-xs text-muted hover:text-brand-600 dark:hover:text-brand-400"
            >
              {endpoint.isActive ? "Pause" : "Resume"}
            </button>

            <button
              type="button"
              onClick={() => remove.mutate()}
              disabled={remove.isPending}
              title="Deletes the endpoint and anything queued for it. Not recoverable."
              className="text-xs text-sla-breached hover:underline"
            >
              Delete
            </button>
          </div>

          <Deliveries endpointId={endpoint.id} pull={pull} />
        </div>
      ) : null}
    </li>
  );
}

/** The one-glance answer to "is this working". */
function Health({ endpoint }: { endpoint: WebhookEndpoint }) {
  if (!endpoint.isActive) {
    return <span className="text-[11px] text-faint">Paused</span>;
  }

  if (endpoint.mode === "pull") return <FeedHealth endpoint={endpoint} />;

  if (endpoint.consecutiveFailures > 0) {
    return (
      <span
        className="text-[11px] text-sla-breached"
        title={endpoint.lastError ?? undefined}
      >
        {endpoint.consecutiveFailures} failed
        {endpoint.lastFailureAt
          ? ` · ${relativeTime(endpoint.lastFailureAt)}`
          : ""}
      </span>
    );
  }

  if (endpoint.lastSuccessAt) {
    return (
      <span className="text-[11px] text-sla-ok">
        Sent {relativeTime(endpoint.lastSuccessAt)}
      </span>
    );
  }

  return <span className="text-[11px] text-faint">Nothing sent yet</span>;
}

/**
 * A feed is healthy when its reader keeps asking. How much is waiting matters
 * less than whether anybody is coming for it -- a backlog a reader is working
 * through is fine, one nobody has read for an hour is the problem.
 */
function FeedHealth({ endpoint }: { endpoint: WebhookEndpoint }) {
  const waiting =
    endpoint.pendingEvents > 0 ? ` · ${endpoint.pendingEvents} waiting` : "";

  if (!endpoint.lastPolledAt) {
    return (
      <span className="text-[11px] text-sla-warning">Never read{waiting}</span>
    );
  }

  const stale =
    Date.now() - new Date(endpoint.lastPolledAt).getTime() > STALE_READER_MS;

  return (
    <span
      className={cn("text-[11px]", stale ? "text-sla-warning" : "text-sla-ok")}
      title={
        endpoint.lastSuccessAt
          ? `Last acknowledged ${shortDateTime(endpoint.lastSuccessAt)}`
          : "Nothing acknowledged yet"
      }
    >
      Read {relativeTime(endpoint.lastPolledAt)}
      {waiting}
    </span>
  );
}

// ── Delivery history ─────────────────────────────────────────────────────────

/**
 * Recent batches, newest first.
 *
 * Fetched only when the endpoint is expanded: it is the screen somebody opens
 * when investigating, not something every render needs, and each row carries its
 * events with it.
 */
function Deliveries({ endpointId, pull }: { endpointId: number; pull: boolean }) {
  const deliveries = useQuery({
    queryKey: queryKeys.crmWebhookDeliveries(endpointId),
    queryFn: () => crmApi.webhookDeliveries(endpointId),
    staleTime: 15_000,
  });

  if (deliveries.isLoading) {
    return <p className="text-[11px] text-faint">Loading deliveries…</p>;
  }

  const rows = deliveries.data ?? [];

  if (rows.length === 0) {
    return (
      <p className="text-[11px] text-faint">
        {pull
          ? "Nothing acknowledged yet. Each acknowledgement from the reader appears here as one row."
          : "Nothing sent yet. A batch appears here within a minute of the first change this endpoint subscribes to."}
      </p>
    );
  }

  return (
    <ul className="divide-y divide-line rounded border border-line">
      {rows.map((row) => (
        <DeliveryRow key={row.id} delivery={row} />
      ))}
    </ul>
  );
}

function DeliveryRow({ delivery }: { delivery: WebhookDelivery }) {
  const [open, setOpen] = useState(false);

  const tone =
    delivery.status === "delivered"
      ? "text-sla-ok"
      : delivery.status === "failed"
        ? "text-sla-breached"
        : "text-muted";

  return (
    <li className="px-2 py-1.5 text-[11px]">
      <button
        type="button"
        onClick={() => setOpen((value) => !value)}
        className="flex w-full flex-wrap items-center gap-2 text-left"
      >
        <span className={cn("w-16 font-medium", tone)}>{delivery.status}</span>

        <span className="text-muted">
          {delivery.eventCount} {delivery.eventCount === 1 ? "event" : "events"}
        </span>

        <span className="text-faint">{shortDateTime(delivery.createdAt)}</span>

        {delivery.attempts > 1 ? (
          <span className="text-faint">{delivery.attempts} attempts</span>
        ) : null}

        {delivery.responseStatus ? (
          <span className="text-faint">HTTP {delivery.responseStatus}</span>
        ) : null}

        {/* A pending batch with a future retry is the state people misread as
            stuck, so it says when it will try again rather than just "pending". */}
        {delivery.status === "pending" && delivery.nextAttemptAt ? (
          <span className="text-faint">
            retries {relativeTime(delivery.nextAttemptAt)}
          </span>
        ) : null}

        <code className="ml-auto text-faint">{delivery.batchId.slice(0, 8)}</code>
      </button>

      {delivery.lastError ? (
        <p className="mt-1 text-sla-breached">{delivery.lastError}</p>
      ) : null}

      {open ? (
        <ul className="mt-1 flex flex-col gap-0.5 border-l border-line pl-2">
          {delivery.events.map((event) => (
            <li key={event.id} className="flex flex-wrap items-center gap-2">
              <span className="text-content">
                {EVENT_LABELS[event.type] ?? event.type}
              </span>
              <span className="text-faint">#{event.objectId}</span>
              {event.actorName ? (
                <span className="text-faint">{event.actorName}</span>
              ) : (
                <span className="text-faint">automatic</span>
              )}
              <code className="ml-auto text-faint">{event.id}</code>
            </li>
          ))}
        </ul>
      ) : null}
    </li>
  );
}

// ── Adding one ───────────────────────────────────────────────────────────────

function NewEndpointForm({
  eventTypes,
  onDone,
}: {
  eventTypes: WebhookEventType[];
  onDone: () => void;
}) {
  const [name, setName] = useState("");
  const [mode, setMode] = useState<WebhookMode>("push");
  const [url, setUrl] = useState("");
  /* Everything ticked to start with: the common case is "tell me about the CRM",
     and unticking three is less work than ticking four. */
  const [events, setEvents] = useState<WebhookEventType[]>(eventTypes);

  const create = useMutation({
    mutationFn: () =>
      crmApi.createWebhook({
        name: name.trim(),
        mode,
        url: mode === "push" ? url.trim() : null,
        events,
      }),
    onSuccess: onDone,
  });

  const toggle = (type: WebhookEventType): void =>
    setEvents((current) =>
      current.includes(type)
        ? current.filter((event) => event !== type)
        : [...current, type],
    );

  return (
    <form
      onSubmit={(event) => {
        event.preventDefault();
        create.mutate();
      }}
      className="mb-2 flex flex-col gap-2 rounded-lg border border-line bg-app p-3"
    >
      <div className="flex flex-wrap items-end gap-2">
        <label className="flex flex-col gap-1">
          <span className="text-[11px] uppercase tracking-wide text-faint">
            Name
          </span>
          <input
            value={name}
            onChange={(event) => setName(event.target.value)}
            autoFocus
            required
            placeholder="Warehouse sync"
            className="h-8 w-48 rounded-md border border-line bg-surface px-2 text-sm text-content outline-none focus:border-brand-400"
          />
        </label>

        <fieldset className="flex flex-col gap-1">
          <legend className="mb-1 text-[11px] uppercase tracking-wide text-faint">
            Delivery
          </legend>
          <div className="flex h-8 items-center gap-3 text-xs text-content">
            {(["push", "pull"] as const).map((option) => (
              <label key={option} className="flex items-center gap-1">
                <input
                  type="radio"
                  name="webhook-mode"
                  checked={mode === option}
                  onChange={() => setMode(option)}
                />
                {option === "push" ? "Send to a URL" : "Read from a feed"}
              </label>
            ))}
          </div>
        </fieldset>

        {mode === "push" ? (
          <label className="flex flex-1 flex-col gap-1">
            <span className="text-[11px] uppercase tracking-wide text-faint">
              URL
            </span>
            <input
              value={url}
              onChange={(event) => setUrl(event.target.value)}
              required
              placeholder="https://example.com/hooks/pulse-crm"
              className="h-8 w-full min-w-64 rounded-md border border-line bg-surface px-2 font-mono text-xs text-content outline-none focus:border-brand-400"
            />
          </label>
        ) : (
          <div className="flex-1" />
        )}

        <Button
          type="submit"
          size="sm"
          disabled={
            create.isPending ||
            !name.trim() ||
            (mode === "push" && !url.trim()) ||
            !events.length
          }
        >
          {create.isPending ? "Adding…" : "Add"}
        </Button>
      </div>

      <div className="flex flex-wrap items-center gap-1">
        {eventTypes.map((type) => {
          const on = events.includes(type);
          return (
            <button
              key={type}
              type="button"
              onClick={() => toggle(type)}
              title={EVENT_HINTS[type]}
              className={cn(
                "rounded-full border px-2 py-0.5 text-[11px]",
                on
                  ? "border-brand-400 bg-brand-50 text-brand-700 dark:border-brand-700 dark:bg-brand-950 dark:text-brand-300"
                  : "border-line text-faint hover:border-brand-300 hover:text-muted",
              )}
            >
              {EVENT_LABELS[type] ?? type}
            </button>
          );
        })}
      </div>

      {create.isError ? (
        <p className="text-xs text-sla-breached">
          {create.error instanceof ApiError
            ? create.error.message
            : "Could not add that endpoint."}
        </p>
      ) : null}

      <p className="text-[11px] text-faint">
        {mode === "push"
          ? "The URL must be https — the payload carries customer names and account numbers. A signing secret is generated when you add it; reveal it under Details and give it to whoever built the receiver."
          : "Nothing is sent. Changes wait until the subscriber reads GET /api/v1/crm/feeds/{id}/events with its API key and the feed secret, then acknowledges them — so an outage on its side loses nothing. Which it is cannot be changed later."}
      </p>
    </form>
  );
}
